import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as yup from "yup";

// Interface
import { ITask } from "../../interface/task";
import { ILot } from "../../interface/lot";
import { MatriceWrapper } from "../../interface/matrice";
import { IProject } from "../../interface/project";

// Material UI
import { 
  Box, Alert, CircularProgress, Dialog, TextField, Modal, Typography, 
  Button, Fade, DialogActions, DialogContent, DialogTitle, Tooltip, 
  Stack, Paper, Divider 
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";

// Icons
import GridOnIcon from "@mui/icons-material/GridOn";
import AddIcon from "@mui/icons-material/Add";
import SendTwoToneIcon from '@mui/icons-material/SendTwoTone';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';

// Redux & Actions
import { RootState } from "../../redux/reducers/index";
import {
  addCharges, sessionEnd, addTask, getLotData, initCalculCost,
  initEmptyCosts, showSnackbar, calculAll, calculTCosts, changeValue,
} from "../../redux/actions/index";

// Components
import TaskDetails from "../../components/Tasks/TaskDetails";
import LotCosts from "../../components/Lots/LotCosts";
import AddLot from "../../components/Lots/AddLot";
import AlerteDialog from "../../components/AlerteDialog/AlerteDialog";
import SelectLots from "../../components/Lots/SelectLots";
import AddLotButton from "../../components/Lots/CreateLotInLotDetails";
import { matrixFormat } from "../../components/Matrix/MatrixFormat";

// Hooks & Fetch
import { fetchPutProtect } from "../../fetchs/put";
import { fetchPostProtect } from "../../fetchs/post";
import { deleteOneLot } from "../../fetchs/delete";
import { getOne, getAll, fetchGetProtect } from "../../fetchs/get";
import useWindowDimensions from "../../tools/useWindowDimensions";

const FK_STATUT = { BROUILLON: 0, VALIDE: 1, CLOS: 2 };

const LotDetails = ({
  project,
  lotId,
  setLotId,
  showDialog,
  setShowDialog,
  refreshProject,
  setOpenModal
}: {
  project: IProject;
  lotId: string;
  setLotId: Function;
  showDialog: boolean;
  setShowDialog: Function;
  refreshProject: Function;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  // Redux States
  const { tasks, charges, tasksCharges, totalCharges, PPR, lotCosts, total } = useSelector((state: RootState) => state.lotSynthesis);
  const token = useSelector((state: RootState) => state.user.token);
   const isClos = Number(project?.syncMetadata?.fkStatut) === FK_STATUT.CLOS;
   const isTmaProject = project.isTma !== undefined ? project.isTma : !!(project.name && project.name.toLowerCase().includes("tma"));
  
  const [error, setError] = useState<string>("");
  const [errorModal, setErrorModal] = useState<string>("");
  const [openModalLot, setOpenModalLot] = useState<boolean>(false);
  const [showModal, setShowModal] = useState(false);
  const [lot, setLot] = useState<ILot | undefined>(undefined);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState<boolean>(false);
  const [valid, setValid] = useState<boolean>(false);
  const [tempLot, setTempLot] = useState<ILot | undefined>(undefined);
  const [editedLotName, setEditedLotName] = useState<string | undefined>("");
  const [validDelete, setValidDelete] = useState<boolean>(false);
  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [assignedUser, setAssignedUser] = useState<any>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [usersLoading, setUsersLoading] = useState<boolean>(false);
  const [assignError, setAssignError] = useState<string>("");

  const { width } = useWindowDimensions();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Yup validation
  const validationFormSchema = yup.object().shape({
    name: yup.string().trim().min(3, "Minimum 3 caractères").required("Obligatoire"),
  });

  useEffect(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  useEffect(() => {
    if (validDelete) {
      updateLot();
      setValidDelete(false);
    }
  }, [validDelete]);

  useEffect(() => {
    if (lot && lot.tasks.length > 0) {
      dispatch(addCharges(lot.charges));
      dispatch(getLotData(lot.tasks, lot.PPR, lot.tasksCharges, lot.totalCharges, lot.lotCosts, lot.total));
      dispatch(initCalculCost());
    } else {
      dispatch(initEmptyCosts());
    }
  }, [lot, lotId, dispatch]);

  useEffect(() => { getLot(); }, [lotId]);

  const getLot = () => {
    if (token) {
      getOne(token, "/lots/", lotId, (lotData: ILot) => {
          setLot(lotData);
          setEditedLotName(lotData.name);
          if (lotData.assignedUser) {
            setAssignedUser(lotData.assignedUser);
          }
          getOne(token, "/matrice/id/", lotData.matrice, (matrice: MatriceWrapper) => {
              // On recalcule avec les tarifs à jour de la matrice (ex. matrice Template
              // modifiée depuis le dernier enregistrement du lot) : sans ça, le montant
              // affiché reste l'instantané stocké en base (getLotData ci-dessous), figé
              // au dernier "Enregistrer le lot", jusqu'à ce qu'on passe manuellement par
              // la modale Matrice.
              dispatch(addCharges(matrixFormat(matrice.data)));
              dispatch(calculAll());
              dispatch(calculTCosts());
            }, () => dispatch(sessionEnd()));
        },
        () => dispatch(sessionEnd()),
        (err: any) => {
          navigate(`/projets/`);
          dispatch(showSnackbar("Erreur récupération lot", "error"));
        }
      );
    }
  };

  const fetchUsers = () => {
    if (!token) return;
    setUsersLoading(true);
    getAll(
      token,
      "/salary/sync",
      (data: any[]) => { setUsers(data); setUsersLoading(false); },
      () => { dispatch(sessionEnd()); setUsersLoading(false); },
      () => setUsersLoading(false)
    );
  };

  const syncDolibarr = async (): Promise<{ created: number; updated: number; errors: number; total: number } | null> => {
    if (!lot || !token) return null;

    const body = JSON.stringify({
      lotId: lot._id,
      projectId: project._id,
      lotName: lot.name,
      tasksCharges,
      PPR,
      fk_project: project.syncMetadata?.dolibarrId ?? null,
      clientName: (project.client as any)?.name ?? "",
      projectRef: project.syncMetadata?.ref ?? project.name,
      projectTitle: project.syncMetadata?.title ?? project.name,
      fk_user_assign: assignedUser ? Number(assignedUser.dolibarr_id) : undefined,
      fk_user_assign_name: assignedUser ? `${assignedUser.firstname || ""} ${assignedUser.lastname || ""}`.trim() : undefined,
    });

    try {
      const res = await fetchPostProtect(token, "/lots/sync-dolibarr", body);
      if (res.status === 200) {
        const data = await res.json();
        return data.summary || null;
      }
      return null;
    } catch (err) {
      console.error("Erreur sync Dolibarr :", err);
      return null;
    }
  };

  const updateLot = () => {
    if (lot && token) {
      const body = JSON.stringify({
        projectId: project._id,
        lotId: lot._id,
        name: editedLotName || lot.name,
        matrice: lot.matrice,
        tasks, charges, tasksCharges, PPR, totalCharges, lotCosts, total,
        ...(assignedUser ? {
          assignedUser: {
            dolibarr_id: assignedUser.dolibarr_id,
            firstname: assignedUser.firstname || "",
            lastname: assignedUser.lastname || "",
          }
        } : {}),
      });
      fetchPutProtect(token, `/lots/update`, body).then((res: any) => {
        if (res.status === 200) {
          refreshProject();
        } else if (res.status === 401) {
          dispatch(sessionEnd());
        }
      });
    }
  };

  const handleSaveLot = async () => {
    if (!assignedUser) {
      setAssignError("Veuillez sélectionner un responsable avant d'enregistrer");
      return;
    }
    setAssignError("");

    // Vérification du statut réel du projet avant tout enregistrement
    // (cas de concurrence : un autre utilisateur a pu clore/brouillonner le projet entre-temps)
    if (token) {
      try {
        const statusRes = await fetchGetProtect(token, "/projects/", project._id);
        if (statusRes.ok) {
          const data = await statusRes.json();
          const freshStatut = Number(data?.message?.syncMetadata?.fkStatut ?? 0);
          if (freshStatut === FK_STATUT.CLOS) {
            dispatch(showSnackbar("Enregistrement impossible : le projet a été clôturé sur Dolibarr", "error"));
            return;
          }
        }
      } catch {
        // En cas d'erreur réseau on laisse passer
      }
    }

    const summary = await syncDolibarr();
    updateLot();
    setValid(true);

    if (summary) {
      const parts: string[] = [];
      if (summary.created > 0) parts.push(`${summary.created} créée${summary.created > 1 ? "s" : ""}`);
      if (summary.updated > 0) parts.push(`${summary.updated} mise${summary.updated > 1 ? "s" : ""} à jour`);
      if (summary.errors > 0) parts.push(`${summary.errors} en erreur`);
      const taskInfo = parts.length > 0 ? ` — ${summary.total} tâche${summary.total > 1 ? "s" : ""} Dolibarr (${parts.join(", ")})` : "";
      dispatch(showSnackbar(`Lot enregistré avec succès${taskInfo}`, summary.errors > 0 ? "warning" : "success"));
    } else {
      dispatch(showSnackbar("Lot enregistré", "success"));
    }
  };

  const deleteLot = (id: string) => {
    let projectId = window.location.pathname.split("/")[2];
    if (token) {
      deleteOneLot(token, `/projects/delete/${projectId}/${id}`).then((res) => {
        if (res.status === 200) {
          dispatch(showSnackbar("Lot supprimé", "success"));
          setLotId("");
        }
      });
    }
  };

  return (
    <Box sx={{ p: width && width > 1600 ? 2 : 0 }}>
      {lot && (
        <AddLot 
          lot={tempLot || lot} 
          setLot={(newLot: ILot) => { setLot(newLot); setTempLot(newLot); }} 
          openModal={openModalLot} 
          setOpenModal={setOpenModalLot} 
          syncLotMatrice={() => {
            // On ne rappelle pas getLot() ici : il écraserait les tâches en cours
            // d'édition (pas encore enregistrées) avec la version stockée côté
            // serveur, faisant réapparaître l'ancien résultat à l'écran (et le
            // ré-enregistrant ensuite via updateLot()). On se contente de
            // rafraîchir les charges de la matrice, puis on recalcule à partir
            // des tâches actuellement en mémoire.
            if (lot && token) {
              getOne(token, "/matrice/id/", lot.matrice, (matrice: MatriceWrapper) => {
                  dispatch(addCharges(matrixFormat(matrice.data)));
                  dispatch(calculAll());
                  dispatch(calculTCosts());
                  updateLot();
                },
                () => dispatch(sessionEnd())
              );
            }
          }}
        />
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* TOOLBAR ACTIONS */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, bgcolor: '#f1f5f9', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems="center">
          
          <Stack direction="row" spacing={2} alignItems="center">
            <Tooltip title={isClos ? "Projet clos, modification impossible" : ""}>
              <span>
                <Button
                  onClick={() => setShowModal(true)}
                  startIcon={<AddIcon />}
                  variant="contained"
                  disabled={isClos}
                  sx={{ borderRadius: '10px', boxShadow: 'none' }}
                >
                  Tâche
                </Button>
              </span>
            </Tooltip>
            {!isTmaProject && project?.lots?.length > 0 && (
              <SelectLots lots={project.lots} setLotId={setLotId} lotId={lotId || ""} />
            )}
            {!isClos && (
              <Button
                variant="contained"
                onClick={handleSaveLot}
                startIcon={<SendTwoToneIcon />}
                sx={{ borderRadius: '10px', boxShadow: 'none' }}
              >
                {isTmaProject ? "Enregistrer" : "Enregistrer le lot"}
              </Button>
            )}
            {isTmaProject && (
              <Button
                variant="outlined"
                onClick={() => navigate(`/projet/${project._id}/historique`)}
                startIcon={<HistoryIcon />}
                sx={{ borderRadius: '10px', bgcolor: 'white' }}
              >
                Historique
              </Button>
            )}
          </Stack>

          <Stack direction="row" spacing={2} alignItems="center">
            <Tooltip title={isClos ? "Projet clos, modification impossible" : ""}>
              <span>
                <Button
                  onClick={() => setOpenModalLot(true)}
                  startIcon={<GridOnIcon />}
                  variant="outlined"
                  disabled={isClos}
                  sx={{ borderRadius: '10px', bgcolor: 'white' }}
                >
                  Matrice
                </Button>
              </span>
            </Tooltip>
            {!isTmaProject && (
              <Tooltip title={isClos ? "Projet clos, modification impossible" : ""}>
                <span>
                  <AddLotButton setOpenModal={isClos ? () => {} : setOpenModal} disabled={isClos} />
                </span>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </Paper>

      {lot ? (
        <Fade in={true}>
          <Box>
            <TaskDetails
              lotId={lotId}
              modal={openModalLot}
              onSave={valid}
              setShowDialog={setShowDialog}
              updateLot={() => { updateLot(); setValid(true); }}
              setValidDelete={setValidDelete}
              isClos={isClos}  // ← passer isClos à TaskDetails
            />
            
            <Divider sx={{ my: 4 }} />
            
            <LotCosts lot={lot._id} />

            {!isClos && (
              <Stack spacing={2} sx={{ mt: 4, mb: 2 }}>
                <Autocomplete
                    options={users}
                    loading={usersLoading}
                    value={assignedUser}
                    onChange={(_e, val) => { setAssignedUser(val); setAssignError(""); }}
                    onOpen={fetchUsers}
                    getOptionLabel={(u) => `${u.firstname || ""} ${u.lastname || ""} (ID Dolibarr: ${u.dolibarr_id})`.trim()}
                    isOptionEqualToValue={(a, b) => String(a.dolibarr_id) === String(b.dolibarr_id)}
                    sx={{ width: 400 }}
                    componentsProps={{
                      paper: {
                        sx: {
                          bgcolor: 'rgb(255 255 255 / 51%)',
                          backdropFilter: 'blur(4px)',
                          WebkitBackdropFilter: 'blur(4px)',
                          borderRadius: '12px',
                          border: '1px solid rgba(255,255,255,0.5)',
                          boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                        }
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Affecter à *"
                        error={!!assignError}
                        helperText={assignError || ""}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {usersLoading ? <CircularProgress size={16} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleSaveLot}
                    startIcon={<SendTwoToneIcon />}
                    sx={{ borderRadius: '12px', px: 4 }}
                  >
                    Enregistrer le lot
                  </Button>
                  {/* <Button
                    variant="outlined"
                    color="error"
                    size="large"
                    onClick={() => setOpenDeleteConfirm(true)}
                    startIcon={<DeleteOutlineIcon />}
                    sx={{ borderRadius: '12px' }}
                  >
                    Supprimer
                  </Button> */}
                </Stack>
              </Stack>
            )}

            <AlerteDialog
              open={openDeleteConfirm}
              handleClose={() => setOpenDeleteConfirm(false)}
              deleteProject={() => deleteLot(lot._id)}
              title="Supprimer ce lot ?"
              contentText="Cette action est irréversible. Toutes les tâches liées seront supprimées."
            />

            {/* MODAL NOUVELLE TÂCHE */}
            <Modal open={showModal} onClose={() => setShowModal(false)} closeAfterTransition>
              <Fade in={showModal}>
                <Box sx={{
                  position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                  width: 500, bgcolor: 'rgb(255 255 255 / 51%)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
                  borderRadius: '24px', p: 4, boxShadow: '0 24px 48px rgba(220, 217, 217, 0.94)', border: '1px solid rgba(255,255,255,0.5)'
                }}>
                  <Typography variant="h5" fontWeight={700} mb={3}>Nouvelle tâche</Typography>
                  <Stack spacing={3}>
                    <TextField
                      fullWidth label="Nom de la tâche"
                      value={name} onChange={(e) => setName(e.target.value)}
                    />
                    <TextField
                      fullWidth multiline rows={3} label="Description"
                      value={description} onChange={(e) => setDescription(e.target.value)}
                    />
                    {errorModal && <Alert severity="error">{errorModal}</Alert>}
                    <Button 
                      variant="contained" 
                      size="large"
                      onClick={async () => {
                        try {
                          await validationFormSchema.validate({ name });
                          if (tasks.find((t: ITask) => t.name === name)) {
                            setErrorModal("Le nom doit être unique");
                          } else {
                            dispatch(addTask());
                            dispatch(changeValue(name, "name", tasks[tasks.length - 1]?._id + 1 || 1));
                            dispatch(changeValue(description, "description", tasks[tasks.length - 1]?._id + 1 || 1));
                            setShowModal(false);
                            setName(""); setDescription(""); setErrorModal("");
                          }
                        } catch (e: any) { setErrorModal(e.errors[0]); }
                      }}
                      sx={{ borderRadius: '12px' }}
                    >
                      Ajouter au lot
                    </Button>
                  </Stack>
                </Box>
              </Fade>
            </Modal>
          </Box>
        </Fade>
      ) : (
        <Box sx={{ display: "flex", flexDirection: 'column', py: 10, alignItems: "center", gap: 2 }}>
          <CircularProgress />
          <Typography color="text.secondary">Chargement du lot...</Typography>
        </Box>
      )}
    </Box>
  );
};

export default LotDetails;